// client/src/coms/views/canvas_parts/CanvasView.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiDelete, apiGet, apiPost, comsApiGet } from '../../comsApi';
import CanvasRoomView from './CanvasRoomView';
import PartyArcadeView from './party/PartyArcadeView';

function formatDateTime(value) {
  try {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function pickCanvasId(canvas) {
  const n = Number(canvas && canvas.id);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function pickCanvasName(canvas) {
  const s = String((canvas && canvas.name) || '').trim();
  return s || 'Untitled Canvas';
}

function pickOwnerId(canvas) {
  if (!canvas || typeof canvas !== 'object') return 0;
  const n = Number(canvas.owner_user_id || canvas.ownerUserId || canvas.ownerId);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function pickParticipantIds(canvas) {
  const raw = canvas && canvas.participant_user_ids;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => Number(v))
    .filter((v) => Number.isInteger(v) && v > 0);
}

function getCanvasMemberIds(canvas) {
  const ids = [pickOwnerId(canvas), ...pickParticipantIds(canvas)]
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0);
  return Array.from(new Set(ids)).sort((a, b) => a - b);
}

function getCanvasMemberSignature(canvas) {
  return getCanvasMemberIds(canvas).join(':');
}


function pickCanvasWorkbookRootId(canvas) {
  const explicit = Number(canvas && (canvas.workbook_root_id ?? canvas.master_canvas_id));
  if (Number.isInteger(explicit) && explicit > 0) return explicit;
  return pickCanvasId(canvas);
}

function getCanvasWorkbookKey(canvas) {
  const rootId = pickCanvasWorkbookRootId(canvas);
  return rootId > 0 ? `canvas-workbook-${rootId}` : getCanvasMemberSignature(canvas);
}

function getCanvasWorkbookCanvases(canvases, canvas) {
  const rootId = pickCanvasWorkbookRootId(canvas);
  if (rootId > 0) {
    return (Array.isArray(canvases) ? canvases : []).filter(
      (candidate) => pickCanvasWorkbookRootId(candidate) === rootId
    );
  }
  const signature = getCanvasMemberSignature(canvas);
  return (Array.isArray(canvases) ? canvases : []).filter(
    (candidate) => getCanvasMemberSignature(candidate) === signature
  );
}

function getCanvasCardRepresentatives(canvases) {
  const groups = new Map();
  (Array.isArray(canvases) ? canvases : []).forEach((canvas) => {
    const rootId = pickCanvasWorkbookRootId(canvas);
    if (!rootId) return;
    const current = groups.get(rootId);
    const canvasId = pickCanvasId(canvas);
    if (!current || canvasId === rootId || pickCanvasId(current) !== rootId) {
      groups.set(rootId, canvas);
    }
  });
  return Array.from(groups.values()).sort((a, b) => {
    const aUpdated = Date.parse(a && a.updated_at) || pickCanvasId(a);
    const bUpdated = Date.parse(b && b.updated_at) || pickCanvasId(b);
    return bUpdated - aUpdated;
  });
}

function pickMaxParticipants(canvas) {
  const n = Number(canvas && canvas.max_participants);
  if (Number.isInteger(n) && n > 0) return n;
  return 4;
}

function pickFriendUserId(friend) {
  const n = Number(friend && friend.other_user_id);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function pickFriendUsername(friend) {
  const s = String((friend && friend.other_user && friend.other_user.username) || '').trim();
  return s || `User ${pickFriendUserId(friend)}`;
}

function initialsFromName(name) {
  const s = String(name || '').trim();
  if (!s) return 'CA';
  const parts = s.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part.charAt(0)).join('');
  return String(letters || s.slice(0, 2)).toUpperCase();
}

function gradientFromId(id) {
  const n = Number(id) || 0;
  const gradients = [
    'linear-gradient(135deg, rgba(99,102,241,0.92), rgba(59,130,246,0.82))',
    'linear-gradient(135deg, rgba(236,72,153,0.92), rgba(168,85,247,0.82))',
    'linear-gradient(135deg, rgba(16,185,129,0.92), rgba(6,182,212,0.82))',
    'linear-gradient(135deg, rgba(245,158,11,0.92), rgba(239,68,68,0.82))',
    'linear-gradient(135deg, rgba(34,197,94,0.92), rgba(59,130,246,0.82))',
  ];
  return gradients[Math.abs(n) % gradients.length];
}

function normaliseCanvasList(payload) {
  const list = Array.isArray(payload && payload.canvases) ? payload.canvases : [];
  return list.filter((item) => pickCanvasId(item) > 0);
}

function normaliseFriendList(payload) {
  const list = Array.isArray(payload && payload.friends) ? payload.friends : [];
  return list.filter((item) => pickFriendUserId(item) > 0);
}


const CANVAS_TAB_ORDER_STORAGE_KEY = 'voxemera-canvas-tab-order-v1';
const CANVAS_STATE_DB_NAME = 'voxemera-canvas-room-local-state';
const CANVAS_STATE_STORE_NAME = 'canvasStates';
const CANVAS_STATE_KEY_PREFIX = 'voxemera-canvas-room-state-v2';

function getCanvasLocalStatePrefix(canvasId) {
  const id = Number(canvasId);
  return Number.isInteger(id) && id > 0 ? `${CANVAS_STATE_KEY_PREFIX}:${id}` : '';
}

function cloneCanvasPayload(value) {
  if (typeof structuredClone === 'function') {
    try { return structuredClone(value); } catch {}
  }
  try { return JSON.parse(JSON.stringify(value)); } catch { return value; }
}

function copyCanvasLocalState(sourceCanvasId, targetCanvasId) {
  const sourcePrefix = getCanvasLocalStatePrefix(sourceCanvasId);
  const targetPrefix = getCanvasLocalStatePrefix(targetCanvasId);
  if (!sourcePrefix || !targetPrefix || typeof window === 'undefined' || !window.indexedDB) {
    return Promise.resolve(false);
  }

  return new Promise((resolve, reject) => {
    const openRequest = window.indexedDB.open(CANVAS_STATE_DB_NAME, 2);
    openRequest.onupgradeneeded = () => {
      const db = openRequest.result;
      if (!db.objectStoreNames.contains(CANVAS_STATE_STORE_NAME)) {
        db.createObjectStore(CANVAS_STATE_STORE_NAME, { keyPath: 'key' });
      }
    };
    openRequest.onerror = () => reject(openRequest.error || new Error('Unable to open canvas storage.'));
    openRequest.onsuccess = () => {
      const db = openRequest.result;
      const readTransaction = db.transaction(CANVAS_STATE_STORE_NAME, 'readonly');
      const readRequest = readTransaction.objectStore(CANVAS_STATE_STORE_NAME).getAll();
      readRequest.onerror = () => {
        try { db.close(); } catch {}
        reject(readRequest.error || new Error('Unable to read canvas storage.'));
      };
      readRequest.onsuccess = () => {
        const sourceRecords = (Array.isArray(readRequest.result) ? readRequest.result : [])
          .filter((record) => String(record && record.key || '').startsWith(sourcePrefix));
        if (!sourceRecords.length) {
          db.close();
          resolve(false);
          return;
        }

        const writeTransaction = db.transaction(CANVAS_STATE_STORE_NAME, 'readwrite');
        const store = writeTransaction.objectStore(CANVAS_STATE_STORE_NAME);
        sourceRecords.forEach((record) => {
          const sourceKey = String(record.key || '');
          const targetKey = `${targetPrefix}${sourceKey.slice(sourcePrefix.length)}`;
          store.put({
            ...record,
            key: targetKey,
            payload: cloneCanvasPayload(record.payload),
            savedAt: Date.now(),
          });
        });
        writeTransaction.oncomplete = () => {
          db.close();
          resolve(true);
        };
        writeTransaction.onerror = () => {
          try { db.close(); } catch {}
          reject(writeTransaction.error || new Error('Unable to copy canvas storage.'));
        };
      };
    };
  });
}

function readCanvasTabOrders() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CANVAS_TAB_ORDER_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeCanvasTabOrder(signature, orderedIds) {
  if (typeof window === 'undefined' || !signature) return;
  try {
    const allOrders = readCanvasTabOrders();
    allOrders[signature] = Array.from(new Set((orderedIds || []).map(Number).filter((id) => id > 0)));
    window.localStorage.setItem(CANVAS_TAB_ORDER_STORAGE_KEY, JSON.stringify(allOrders));
  } catch {}
}

function sortCanvasWorkbookTabs(canvases, signature) {
  const list = Array.isArray(canvases) ? [...canvases] : [];
  const storedOrder = typeof window !== 'undefined' && signature
    ? readCanvasTabOrders()[signature]
    : null;
  const orderIndex = new Map((Array.isArray(storedOrder) ? storedOrder : []).map((id, index) => [Number(id), index]));

  return list.sort((a, b) => {
    const aId = pickCanvasId(a);
    const bId = pickCanvasId(b);
    const aStored = orderIndex.has(aId);
    const bStored = orderIndex.has(bId);
    if (aStored && bStored) return orderIndex.get(aId) - orderIndex.get(bId);
    if (aStored) return -1;
    if (bStored) return 1;
    const aCreated = Date.parse(a && a.created_at) || aId;
    const bCreated = Date.parse(b && b.created_at) || bId;
    return aCreated - bCreated;
  });
}

function makeCanvasCreationName(date = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date).replace(',', '');
  } catch {
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }
}

async function patchCanvas(canvasId, body) {
  const response = await fetch(`/api/canvas/${Number(canvasId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Failed to update canvas.');
  return payload;
}

function CanvasCard({ canvas, currentUserId, onOpen, onDelete, onInvite, deleteBusy }) {
  const canvasId = pickCanvasId(canvas);
  const name = pickCanvasName(canvas);
  const ownerId = pickOwnerId(canvas);
  const participantIds = pickParticipantIds(canvas);
  const maxParticipants = pickMaxParticipants(canvas);
  const totalPeople = Math.min(maxParticipants, 1 + participantIds.length);
  const updatedText = formatDateTime(canvas && canvas.updated_at);
  const createdText = formatDateTime(canvas && canvas.created_at);
  const isOwner = currentUserId > 0 && ownerId === currentUserId;
  const inviteDisabled = !isOwner || totalPeople >= maxParticipants;

  return (
    <div
      style={{
        width: '100%',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 20,
        background: 'rgba(255,255,255,0.04)',
        boxShadow: '0 14px 40px rgba(0,0,0,0.26)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => onOpen(canvas)}
        style={{
          width: '100%',
          display: 'block',
          border: 0,
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <div
          style={{
            height: 156,
            background: gradientFromId(canvasId),
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            padding: 16,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'rgba(255,255,255,0.16)',
              border: '1px solid rgba(255,255,255,0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
              fontSize: 20,
              fontWeight: 900,
              letterSpacing: '0.03em',
              boxShadow: '0 10px 30px rgba(0,0,0,0.22)',
              backdropFilter: 'blur(4px)',
            }}
          >
            {initialsFromName(name)}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 28,
                padding: '0 10px',
                borderRadius: 999,
                background: 'rgba(0,0,0, calc(0.22 * var(--vx-sh, 1)))',
                border: '1px solid rgba(255,255,255,0.18)',
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.02em',
              }}
            >
              {totalPeople} / {maxParticipants}
            </span>

            {isOwner ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 28,
                  padding: '0 10px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.16)',
                  border: '1px solid rgba(255,255,255,0.20)',
                  color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                Owner
              </span>
            ) : null}
          </div>
        </div>

        <div style={{ padding: 16 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 900,
              lineHeight: 1.2,
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
              marginBottom: 8,
              wordBreak: 'break-word',
            }}
          >
            {name}
          </div>

          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.66 * var(--vx-ov, 1)))',
              minHeight: 38,
              marginBottom: 12,
            }}
          >
            {updatedText
              ? `Updated ${updatedText}`
              : createdText
                ? `Created ${createdText}`
                : 'Private collaborative canvas'}
          </div>

          <div
            style={{
              fontSize: 12,
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.52 * var(--vx-ov, 1)))',
              fontWeight: 700,
              letterSpacing: '0.02em',
            }}
          >
            ID {canvasId}
          </div>
        </div>
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '0 16px 16px',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => onOpen(canvas)}
          style={{
            height: 38,
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.08)',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.94 * var(--vx-ov, 1)))',
            padding: '0 14px',
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Open canvas
        </button>

        {isOwner ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={() => onInvite(canvas)}
              disabled={inviteDisabled}
              style={{
                height: 38,
                borderRadius: 12,
                border: '1px solid rgba(96,165,250,0.22)',
                background: inviteDisabled ? 'rgba(30,64,175,0.16)' : 'rgba(30,64,175,0.34)',
                color: inviteDisabled ? 'rgba(191,219,254,0.46)' : 'rgba(219,234,254,0.98)',
                padding: '0 14px',
                fontSize: 13,
                fontWeight: 800,
                cursor: inviteDisabled ? 'default' : 'pointer',
              }}
            >
              Invite friend
            </button>

            <button
              type="button"
              onClick={() => onDelete(canvas)}
              disabled={deleteBusy}
              style={{
                height: 38,
                borderRadius: 12,
                border: '1px solid rgba(248,113,113,0.20)',
                background: deleteBusy ? 'rgba(127,29,29,0.22)' : 'rgba(127,29,29,0.30)',
                color: 'rgba(255,230,230,0.96)',
                padding: '0 14px',
                fontSize: 13,
                fontWeight: 800,
                cursor: deleteBusy ? 'default' : 'pointer',
              }}
            >
              {deleteBusy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}

function InviteFriendModal({
  canvas,
  friends,
  loading,
  busyUserId,
  error,
  currentUserId,
  onClose,
  onInviteFriend,
}) {
  const canvasName = pickCanvasName(canvas);
  const participantIds = pickParticipantIds(canvas);
  const ownerId = pickOwnerId(canvas);
  const maxParticipants = pickMaxParticipants(canvas);
  const totalPeople = Math.min(maxParticipants, 1 + participantIds.length);
  const participantSet = useMemo(() => {
    return new Set([ownerId, ...participantIds].filter((id) => Number.isInteger(Number(id)) && Number(id) > 0).map(Number));
  }, [ownerId, participantIds]);

  const availableFriends = useMemo(() => {
    return friends.filter((friend) => {
      const friendUserId = pickFriendUserId(friend);
      return friendUserId > 0 && friendUserId !== currentUserId && !participantSet.has(friendUserId);
    });
  }, [friends, currentUserId, participantSet]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0, calc(0.62 * var(--vx-sh, 1)))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        boxSizing: 'border-box',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 460,
          maxWidth: '100%',
          maxHeight: '86vh',
          overflow: 'hidden',
          borderRadius: 20,
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(15,23,42,0.98)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.48)',
        }}
      >
        <div
          style={{
            padding: 18,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 20,
                lineHeight: 1.2,
                fontWeight: 900,
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
                marginBottom: 6,
              }}
            >
              Invite friend
            </div>

            <div
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.66 * var(--vx-ov, 1)))',
              }}
            >
              {canvasName} · {totalPeople} / {maxParticipants}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: 34,
              height: 34,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.84 * var(--vx-ov, 1)))',
              fontSize: 18,
              fontWeight: 900,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        {error ? (
          <div
            style={{
              margin: 14,
              padding: '11px 12px',
              borderRadius: 14,
              border: '1px solid rgba(248,113,113,0.25)',
              background: 'rgba(127,29,29,0.22)',
              color: 'rgba(255,220,220,0.96)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            padding: 14,
            maxHeight: '58vh',
            overflow: 'auto',
          }}
        >
          {loading ? (
            <div
              style={{
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.70 * var(--vx-ov, 1)))',
                fontSize: 14,
                fontWeight: 800,
              }}
            >
              Loading friends…
            </div>
          ) : availableFriends.length ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {availableFriends.map((friend) => {
                const friendUserId = pickFriendUserId(friend);
                const username = pickFriendUsername(friend);
                const busy = busyUserId === friendUserId;

                return (
                  <div
                    key={friendUserId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: 12,
                      borderRadius: 16,
                      border: '1px solid rgba(255,255,255,0.08)',
                      background: 'rgba(255,255,255,0.04)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 14,
                          background: gradientFromId(friendUserId),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
                          fontSize: 14,
                          fontWeight: 900,
                          flex: '0 0 auto',
                        }}
                      >
                        {initialsFromName(username)}
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
                            fontSize: 14,
                            fontWeight: 900,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {username}
                        </div>

                        <div
                          style={{
                            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.50 * var(--vx-ov, 1)))',
                            fontSize: 12,
                            fontWeight: 700,
                            marginTop: 2,
                          }}
                        >
                          User {friendUserId}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => onInviteFriend(friend)}
                      disabled={busy}
                      style={{
                        height: 36,
                        borderRadius: 12,
                        border: '1px solid rgba(96,165,250,0.22)',
                        background: busy ? 'rgba(30,64,175,0.16)' : 'rgba(30,64,175,0.34)',
                        color: busy ? 'rgba(191,219,254,0.50)' : 'rgba(219,234,254,0.98)',
                        padding: '0 13px',
                        fontSize: 13,
                        fontWeight: 900,
                        cursor: busy ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {busy ? 'Inviting…' : 'Invite'}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                minHeight: 140,
                borderRadius: 16,
                border: '1px dashed rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.025)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                textAlign: 'center',
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.64 * var(--vx-ov, 1)))',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              No available friends to invite.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CanvasView({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Inside the Voxemera shell (/comms/...) the canvas list lives at the shell's own path; leaving for
  // /canvas would replace the whole application.
  const inShell = !String(location.pathname || '').startsWith('/canvas');
  const basePath = inShell ? location.pathname : '/canvas';
  const [canvases, setCanvases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createBusy, setCreateBusy] = useState(false);
  const [cloneBusy, setCloneBusy] = useState(false);
  const [canvasWorkbookSignatures, setCanvasWorkbookSignatures] = useState({});
  const [canvasTabOrderVersion, setCanvasTabOrderVersion] = useState(0);
  const [deleteCanvasId, setDeleteCanvasId] = useState(0);
  const [error, setError] = useState('');
  const [creatingName, setCreatingName] = useState('');
  const [inviteCanvas, setInviteCanvas] = useState(null);
  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [inviteBusyUserId, setInviteBusyUserId] = useState(0);
  const [inviteError, setInviteError] = useState('');
  const canvasRefreshInFlightRef = useRef(false);
  const lastVisibleCanvasTabsRef = useRef([]);
  const missingCanvasCheckRef = useRef({ id: 0, pending: false });

  const currentUserId = useMemo(() => {
    const n = Number(user && user.id);
    return Number.isInteger(n) && n > 0 ? n : 0;
  }, [user]);

  const selectedCanvasId = useMemo(() => {
    const raw = String(searchParams.get('canvasId') || '').trim();
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : 0;
  }, [searchParams]);

  // Party games: ?arcade=1, ?party=<session>, ?partyCode=<invite code> or ?partyResult=<gallery>.
  const partyInitial = useMemo(() => ({
    arcade: searchParams.get('arcade') === '1',
    sessionId: String(searchParams.get('party') || ''),
    code: String(searchParams.get('partyCode') || ''),
    resultId: String(searchParams.get('partyResult') || ''),
  }), [searchParams]);
  const showPartyGames = partyInitial.arcade || Boolean(partyInitial.sessionId || partyInitial.code || partyInitial.resultId);

  const openCanvasInUrl = useCallback((canvasId) => {
    const id = Number(canvasId);
    if (!Number.isInteger(id) || id <= 0) return;
    const next = new URLSearchParams(searchParams);
    next.set('canvasId', String(id));
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const closeCanvasInUrl = useCallback(() => {
    navigate(basePath, { replace: false });
  }, [basePath, navigate]);

  const loadCanvases = useCallback(async ({ silent = false } = {}) => {
    if (canvasRefreshInFlightRef.current) return null;
    canvasRefreshInFlightRef.current = true;

    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const payload = await apiGet('/canvas');
      const nextCanvases = normaliseCanvasList(payload);
      setCanvases(nextCanvases);
      return nextCanvases;
    } catch (err) {
      if (!silent) {
        setCanvases([]);
        setError(
          String(
            (err && err.message) ||
            'Failed to load canvases.'
          )
        );
      }
      return null;
    } finally {
      canvasRefreshInFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  }, []);

  const loadFriends = useCallback(async () => {
    setFriendsLoading(true);
    setInviteError('');

    try {
      const payload = await comsApiGet('/friends');
      setFriends(normaliseFriendList(payload));
    } catch (err) {
      setFriends([]);
      setInviteError(
        String(
          (err && err.message) ||
          'Failed to load friends.'
        )
      );
    } finally {
      setFriendsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCanvases().catch(() => {});
  }, [loadCanvases]);

  useEffect(() => {
    if (selectedCanvasId <= 0 || typeof window === 'undefined') return undefined;

    let disposed = false;

    const refreshCanvasTabs = () => {
      if (disposed || (typeof document !== 'undefined' && document.hidden)) return;
      loadCanvases({ silent: true }).catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) refreshCanvasTabs();
    };

    const intervalId = window.setInterval(refreshCanvasTabs, 1500);
    window.addEventListener('focus', refreshCanvasTabs);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshCanvasTabs);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [loadCanvases, selectedCanvasId]);

  useEffect(() => {
    if (loading || selectedCanvasId <= 0) return;

    const selectedStillExists = canvases.some((canvas) => pickCanvasId(canvas) === selectedCanvasId);
    if (selectedStillExists) {
      const selectedCanvas = canvases.find((canvas) => pickCanvasId(canvas) === selectedCanvasId) || null;
      const workbookKey = getCanvasWorkbookKey(selectedCanvas);
      const orderedTabs = sortCanvasWorkbookTabs(
        selectedCanvas ? getCanvasWorkbookCanvases(canvases, selectedCanvas) : [],
        workbookKey,
      );
      lastVisibleCanvasTabsRef.current = orderedTabs;
      return;
    }

    const previousTabs = Array.isArray(lastVisibleCanvasTabsRef.current)
      ? lastVisibleCanvasTabsRef.current
      : [];
    const deletedIndex = previousTabs.findIndex((canvas) => pickCanvasId(canvas) === selectedCanvasId);
    // A canvas this list has never shown may be one the user was just added to (e.g. joined a game by
    // code); refetch once before treating it as deleted.
    const check = missingCanvasCheckRef.current;
    if (deletedIndex < 0 && (check.id !== selectedCanvasId || check.pending)) {
      if (check.pending) return;
      missingCanvasCheckRef.current = { id: selectedCanvasId, pending: true };
      loadCanvases({ silent: true }).then((list) => {
        missingCanvasCheckRef.current = { id: list ? selectedCanvasId : 0, pending: false };
        if (list && !list.some((canvas) => pickCanvasId(canvas) === selectedCanvasId)) closeCanvasInUrl();
      });
      return;
    }
    const leftCanvas = deletedIndex > 0 ? previousTabs[deletedIndex - 1] : null;
    const rightCanvas = deletedIndex >= 0 && deletedIndex + 1 < previousTabs.length
      ? previousTabs[deletedIndex + 1]
      : null;
    const nextCanvas = leftCanvas || rightCanvas || null;

    if (nextCanvas && canvases.some((canvas) => pickCanvasId(canvas) === pickCanvasId(nextCanvas))) {
      openCanvasInUrl(pickCanvasId(nextCanvas));
    } else {
      closeCanvasInUrl();
    }
  }, [
    canvasWorkbookSignatures,
    canvases,
    closeCanvasInUrl,
    loadCanvases,
    loading,
    openCanvasInUrl,
    selectedCanvasId,
  ]);

  async function handleCreateCanvas() {
    if (createBusy) return;

    setCreateBusy(true);
    setError('');

    try {
      const payload = await apiPost('/canvas', {
        name: String(creatingName || '').trim() || 'Untitled Canvas',
        max_participants: 4,
      });

      const created = payload && payload.canvas ? payload.canvas : null;
      if (created && pickCanvasId(created) > 0) {
        const createdId = pickCanvasId(created);
        setCanvases((prev) => [created, ...prev.filter((item) => pickCanvasId(item) !== createdId)]);
        setCreatingName('');
        openCanvasInUrl(createdId);
      } else {
        await loadCanvases();
      }
    } catch (err) {
      setError(
        String(
          (err && err.message) ||
          'Failed to create canvas.'
        )
      );
    } finally {
      setCreateBusy(false);
    }
  }

  async function handleDeleteCanvas(canvas) {
    const id = pickCanvasId(canvas);
    if (!id || deleteCanvasId === id) return;

    const name = pickCanvasName(canvas);

    try {
      const ok = window.confirm(`Delete "${name}"?`);
      if (!ok) return;
    } catch {}

    setDeleteCanvasId(id);
    setError('');

    try {
      await apiDelete(`/canvas/${id}`);
      if (selectedCanvasId === id) {
        const previousTabs = Array.isArray(lastVisibleCanvasTabsRef.current)
          ? lastVisibleCanvasTabsRef.current
          : [];
        const deletedIndex = previousTabs.findIndex((item) => pickCanvasId(item) === id);
        const nextCanvas = (deletedIndex > 0 ? previousTabs[deletedIndex - 1] : null)
          || (deletedIndex >= 0 && deletedIndex + 1 < previousTabs.length ? previousTabs[deletedIndex + 1] : null)
          || null;
        setCanvases((prev) => prev.filter((item) => pickCanvasId(item) !== id));
        if (nextCanvas) openCanvasInUrl(pickCanvasId(nextCanvas));
        else closeCanvasInUrl();
      } else {
        setCanvases((prev) => prev.filter((item) => pickCanvasId(item) !== id));
      }
    } catch (err) {
      setError(
        String(
          (err && err.message) ||
          'Failed to delete canvas.'
        )
      );
    } finally {
      setDeleteCanvasId(0);
    }
  }

  function handleInviteFriend(canvas) {
    const id = pickCanvasId(canvas);
    if (!id) return;
    setInviteCanvas(canvas);
    setInviteError('');
    setInviteBusyUserId(0);
    loadFriends().catch(() => {});
  }

  function handleCloseInvite() {
    if (inviteBusyUserId > 0) return;
    setInviteCanvas(null);
    setInviteError('');
  }

  async function handleInviteSelectedFriend(friend) {
    const canvasId = pickCanvasId(inviteCanvas);
    const friendUserId = pickFriendUserId(friend);

    if (!canvasId || !friendUserId || inviteBusyUserId > 0) return;

    setInviteBusyUserId(friendUserId);
    setInviteError('');

    try {
      const payload = await apiPost(`/canvas/${canvasId}/invite`, {
        user_id: friendUserId,
      });

      const updatedCanvas = payload && payload.canvas ? payload.canvas : null;
      if (updatedCanvas && pickCanvasId(updatedCanvas) > 0) {
        const updatedId = pickCanvasId(updatedCanvas);
        setCanvases((prev) => prev.map((item) => (
          pickCanvasId(item) === updatedId ? updatedCanvas : item
        )));
        setInviteCanvas(updatedCanvas);
      } else {
        await loadCanvases();
      }
    } catch (err) {
      setInviteError(
        String(
          (err && err.message) ||
          'Failed to invite friend.'
        )
      );
    } finally {
      setInviteBusyUserId(0);
    }
  }

  function handleOpenCanvas(canvas) {
    const id = pickCanvasId(canvas);
    if (!id) return;
    openCanvasInUrl(id);
  }

  async function handleCreateCanvasWithSamePeople(sourceCanvas) {
    if (cloneBusy) return;

    const sourceId = pickCanvasId(sourceCanvas);
    if (!sourceId || currentUserId <= 0 || pickOwnerId(sourceCanvas) !== currentUserId) return;

    const peopleToInvite = getCanvasMemberIds(sourceCanvas)
      .filter((userId) => userId !== currentUserId)
      .slice(0, Math.max(0, pickMaxParticipants(sourceCanvas) - 1));

    setCloneBusy(true);
    setError('');

    try {
      const sourceSignature = getCanvasWorkbookKey(sourceCanvas);
      const createPayload = await apiPost('/canvas', {
        name: makeCanvasCreationName(),
        max_participants: pickMaxParticipants(sourceCanvas),
        source_canvas_id: sourceId,
        copy_participants: true,
        participant_user_ids: peopleToInvite,
      });

      let createdCanvas = createPayload && createPayload.canvas ? createPayload.canvas : null;
      const createdId = pickCanvasId(createdCanvas);
      if (!createdId) {
        throw new Error('The new canvas was created without a usable canvas id.');
      }

      setCanvasWorkbookSignatures((prev) => ({
        ...prev,
        [createdId]: sourceSignature,
      }));
      setCanvases((prev) => [
        ...prev.filter((item) => pickCanvasId(item) !== createdId),
        createdCanvas,
      ]);
      const existingWorkbookIds = sortCanvasWorkbookTabs(
        canvases.filter((item) => {
          const itemId = pickCanvasId(item);
          return getCanvasWorkbookKey(item) === sourceSignature;
        }),
        sourceSignature,
      ).map(pickCanvasId);
      writeCanvasTabOrder(sourceSignature, [...existingWorkbookIds, createdId]);
      setCanvasTabOrderVersion((value) => value + 1);
      openCanvasInUrl(createdId);

      for (const userId of peopleToInvite) {
        try {
          const invitePayload = await apiPost(`/canvas/${createdId}/invite`, {
            user_id: userId,
          });
          if (invitePayload && invitePayload.canvas && pickCanvasId(invitePayload.canvas) === createdId) {
            createdCanvas = invitePayload.canvas;
            setCanvases((prev) => prev.map((item) => (
              pickCanvasId(item) === createdId ? createdCanvas : item
            )));
          }
        } catch (inviteError) {
          console.error('Failed to add an existing canvas member to the new canvas.', inviteError);
        }
      }

      await loadCanvases();
    } catch (err) {
      setError(String((err && err.message) || 'Failed to create a canvas with the same people.'));
    } finally {
      setCloneBusy(false);
    }
  }


  async function handleCopyCanvasTab(sourceCanvas) {
    if (cloneBusy) return;

    const sourceId = pickCanvasId(sourceCanvas);
    if (!sourceId || currentUserId <= 0 || pickOwnerId(sourceCanvas) !== currentUserId) return;

    const peopleToInvite = getCanvasMemberIds(sourceCanvas)
      .filter((userId) => userId !== currentUserId)
      .slice(0, Math.max(0, pickMaxParticipants(sourceCanvas) - 1));
    const sourceName = pickCanvasName(sourceCanvas) || 'Untitled Canvas';

    setCloneBusy(true);
    setError('');

    try {
      const sourceSignature = getCanvasWorkbookKey(sourceCanvas);
      const createPayload = await apiPost('/canvas', {
        name: `${sourceName} copy`,
        max_participants: pickMaxParticipants(sourceCanvas),
        source_canvas_id: sourceId,
        copy_participants: true,
        participant_user_ids: peopleToInvite,
      });

      let createdCanvas = createPayload && createPayload.canvas ? createPayload.canvas : null;
      const createdId = pickCanvasId(createdCanvas);
      if (!createdId) throw new Error('The copied canvas was created without a usable canvas id.');

      await copyCanvasLocalState(sourceId, createdId);

      setCanvasWorkbookSignatures((prev) => ({ ...prev, [createdId]: sourceSignature }));
      setCanvases((prev) => [
        ...prev.filter((item) => pickCanvasId(item) !== createdId),
        createdCanvas,
      ]);

      const existingWorkbookIds = sortCanvasWorkbookTabs(
        canvases.filter((item) => {
          const itemId = pickCanvasId(item);
          return getCanvasWorkbookKey(item) === sourceSignature;
        }),
        sourceSignature,
      ).map(pickCanvasId);
      writeCanvasTabOrder(sourceSignature, [...existingWorkbookIds, createdId]);
      setCanvasTabOrderVersion((value) => value + 1);

      for (const userId of peopleToInvite) {
        try {
          const invitePayload = await apiPost(`/canvas/${createdId}/invite`, { user_id: userId });
          if (invitePayload && invitePayload.canvas && pickCanvasId(invitePayload.canvas) === createdId) {
            createdCanvas = invitePayload.canvas;
            setCanvases((prev) => prev.map((item) => (
              pickCanvasId(item) === createdId ? createdCanvas : item
            )));
          }
        } catch (inviteError) {
          console.error('Failed to add an existing canvas member to the copied canvas.', inviteError);
        }
      }

      openCanvasInUrl(createdId);
      await loadCanvases();
    } catch (err) {
      setError(String((err && err.message) || 'Failed to copy the canvas.'));
    } finally {
      setCloneBusy(false);
    }
  }


  async function handleRenameCanvasTab(canvasTab, nextName) {
    const id = pickCanvasId(canvasTab);
    const name = String(nextName || '').trim();
    if (!id || !name) return;
    const payload = await patchCanvas(id, { name });
    const updatedCanvas = payload && payload.canvas ? payload.canvas : { ...canvasTab, name };
    setCanvases((prev) => prev.map((item) => (pickCanvasId(item) === id ? updatedCanvas : item)));
  }

  async function handleDeleteCanvasTab(canvasTab, orderedTabs) {
    const id = pickCanvasId(canvasTab);
    if (!id) return;
    await apiDelete(`/canvas/${id}`);

    const signature = getCanvasWorkbookKey(canvasTab);
    const remainingTabs = (Array.isArray(orderedTabs) ? orderedTabs : []).filter((item) => pickCanvasId(item) !== id);
    writeCanvasTabOrder(signature, remainingTabs.map(pickCanvasId));
    setCanvasTabOrderVersion((value) => value + 1);
    setCanvases((prev) => prev.filter((item) => pickCanvasId(item) !== id));

    if (id === selectedCanvasId) {
      const originalTabs = Array.isArray(orderedTabs) ? orderedTabs : [];
      const deletedIndex = originalTabs.findIndex((item) => pickCanvasId(item) === id);
      const leftCanvas = deletedIndex > 0 ? originalTabs[deletedIndex - 1] : null;
      const rightCanvas = deletedIndex >= 0 && deletedIndex + 1 < originalTabs.length
        ? originalTabs[deletedIndex + 1]
        : null;
      const nextCanvas = leftCanvas || rightCanvas || null;
      if (nextCanvas) openCanvasInUrl(pickCanvasId(nextCanvas));
      else closeCanvasInUrl();
    }
  }

  function handleReorderCanvasTabs(orderedTabs) {
    const safeTabs = Array.isArray(orderedTabs) ? orderedTabs : [];
    if (!safeTabs.length) return;
    const signature = getCanvasWorkbookKey(safeTabs[0]);
    writeCanvasTabOrder(signature, safeTabs.map(pickCanvasId));
    setCanvasTabOrderVersion((value) => value + 1);
  }

  function handleCreateKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateCanvas().catch?.(() => {});
    }
  }

  if (showPartyGames && selectedCanvasId <= 0) {
    return <PartyArcadeView user={user} initial={partyInitial} syncUrl onClose={() => navigate(basePath)} />;
  }

  if (selectedCanvasId > 0) {
    const selectedCanvas = canvases.find((candidate) => pickCanvasId(candidate) === selectedCanvasId) || null;
    const selectedSignature = getCanvasWorkbookKey(selectedCanvas);
    const workbookCanvases = sortCanvasWorkbookTabs(
      selectedCanvas ? getCanvasWorkbookCanvases(canvases, selectedCanvas) : [],
      selectedSignature,
    );
    void canvasTabOrderVersion;

    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {workbookCanvases.map((canvasTab) => {
          const tabCanvasId = pickCanvasId(canvasTab);
          if (!tabCanvasId) return null;
          const isActiveTab = tabCanvasId === selectedCanvasId;

          return (
            <div
              key={`canvas-room-shell-${tabCanvasId}`}
              aria-hidden={!isActiveTab}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                minHeight: 0,
                visibility: isActiveTab ? 'visible' : 'hidden',
                pointerEvents: isActiveTab ? 'auto' : 'none',
                zIndex: isActiveTab ? 2 : 1,
              }}
            >
              <CanvasRoomView
                key={`canvas-room-${tabCanvasId}`}
                canvasId={tabCanvasId}
                canvas={canvasTab}
                canvasTabs={workbookCanvases}
                canvasTabBusy={cloneBusy}
                onOpenCanvasTab={handleOpenCanvas}
                onCreateCanvasTab={() => handleCreateCanvasWithSamePeople(canvasTab)}
                onRenameCanvasTab={handleRenameCanvasTab}
                onCopyCanvasTab={handleCopyCanvasTab}
                onDeleteCanvasTab={handleDeleteCanvasTab}
                onReorderCanvasTabs={handleReorderCanvasTabs}
                user={user}
                onBack={closeCanvasInUrl}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="coms-view" style={{ padding: 18, boxSizing: 'border-box' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 900,
              lineHeight: 1.1,
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
              marginBottom: 8,
            }}
          >
            Canvas
          </div>

          <div
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.70 * var(--vx-ov, 1)))',
              maxWidth: 720,
            }}
          >
            Private collaborative canvases for live group drawing. Create one, invite up to three other people.
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <input
            type="text"
            value={creatingName}
            onChange={(e) => setCreatingName(e.target.value)}
            onKeyDown={handleCreateKeyDown}
            placeholder="Untitled Canvas"
            style={{
              width: 240,
              maxWidth: '100%',
              height: 42,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.05)',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
              padding: '0 14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          <button
            type="button"
            onClick={handleCreateCanvas}
            disabled={createBusy}
            style={{
              height: 42,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.12)',
              background: createBusy ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.12)',
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
              padding: '0 16px',
              fontSize: 14,
              fontWeight: 800,
              cursor: createBusy ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {createBusy ? 'Creating…' : 'Create a Canvas'}
          </button>

          <button
            type="button"
            onClick={() => {
              // In the shell, drawing games live in the shared Games destination.
              if (inShell) {
                window.__voxPendingGamesContext = { category: 'Drawing' };
                window.dispatchEvent(new CustomEvent('vox-open-games', { detail: { category: 'Drawing' } }));
              }
              else navigate('/canvas?arcade=1');
            }}
            data-testid="open-party-games"
            style={{
              height: 42,
              borderRadius: 12,
              border: '1px solid rgba(235,69,158,0.55)',
              background: 'linear-gradient(120deg, #5865f2, #eb459e)',
              color: 'var(--vx-text, #fff)',
              padding: '0 16px',
              fontSize: 14,
              fontWeight: 900,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            🎨 Party Games
          </button>
        </div>
      </div>

      {error ? (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(248,113,113,0.25)',
            background: 'rgba(127,29,29,0.20)',
            color: 'rgba(255,220,220,0.96)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <div
          style={{
            minHeight: 260,
            borderRadius: 20,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.74 * var(--vx-ov, 1)))',
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          Loading canvases…
        </div>
      ) : getCanvasCardRepresentatives(canvases).length ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
            gap: 16,
          }}
        >
          {getCanvasCardRepresentatives(canvases).map((canvas) => {
            const canvasId = pickCanvasId(canvas);
            return (
              <CanvasCard
                key={canvasId}
                canvas={canvas}
                currentUserId={currentUserId}
                onOpen={handleOpenCanvas}
                onDelete={handleDeleteCanvas}
                onInvite={handleInviteFriend}
                deleteBusy={deleteCanvasId === canvasId}
              />
            );
          })}
        </div>
      ) : (
        <div
          style={{
            minHeight: 280,
            borderRadius: 20,
            border: '1px dashed rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.025)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.98 * var(--vx-ov, 1)))',
                marginBottom: 10,
              }}
            >
              No canvases yet
            </div>

            <div
              style={{
                maxWidth: 560,
                fontSize: 14,
                lineHeight: 1.7,
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.64 * var(--vx-ov, 1)))',
              }}
            >
              Create your first private canvas and invite up to three other people into it.
            </div>
          </div>
        </div>
      )}

      {inviteCanvas ? (
        <InviteFriendModal
          canvas={inviteCanvas}
          friends={friends}
          loading={friendsLoading}
          busyUserId={inviteBusyUserId}
          error={inviteError}
          currentUserId={currentUserId}
          onClose={handleCloseInvite}
          onInviteFriend={handleInviteSelectedFriend}
        />
      ) : null}
    </div>
  );
}
